import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataPagination } from "@/components/ui/data-pagination";
import { Switch } from "@/components/ui/switch";
import { Pencil, Trash2 } from "lucide-react";
import { emptyPage, useListParams } from "@/lib/list-query";
import { useTools, useDeleteTool, AgentTool, TOOLS_PAGE_SIZE } from "@/services/tool.query";
import { EditToolDialog } from "./EditToolDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { LiveRuntime } from "@/utils/tool-options";

interface UnifiedToolsListProps {
  agentId: string;
  enabledToolIds: string[];
  onToolsSelectionChanged: (toolIds: string[]) => void;
  /** The runtime of the editor's current (unsaved) model. */
  runtime: LiveRuntime;
}

// Helper function to strip protocol blocks from display description.
// Cleans BOTH the speak ("[CRITICAL PROTOCOL: ... Speak, then execute.") and the
// silent ("[BACKGROUND PROCESS: SILENT EXECUTION] ...") blocks so old tools that
// already have a baked-in protocol get cleaned for display.
const getDisplayDescription = (desc: string | null): string => {
  if (!desc) return '';
  return desc
    .replace(/\[CRITICAL PROTOCOL:[\s\S]*?Speak, then execute\./g, '')
    .replace(/\[BACKGROUND PROCESS:[\s\S]*?(?:Immediate execution is required\.|$)/g, '')
    .replace(/\[DYNAMIC PROMPT\][\s\S]*/g, '')
    .trim();
};

export const UnifiedToolsList = ({ agentId, enabledToolIds, onToolsSelectionChanged, runtime }: UnifiedToolsListProps) => {
  // Paged server-side, a page at a time: an agent with a hundred tools must not
  // download them all to show the first twenty. See lib/list-query.
  const list = useListParams({ size: TOOLS_PAGE_SIZE });
  const { data, isLoading } = useTools(agentId, list.params);
  const page = data ?? emptyPage<AgentTool>();
  const customTools = page.rows;
  // Deleting the last tool on the last page leaves `params.page` past the end.
  const { clampToTotal } = list;
  useEffect(() => {
    if (!isLoading) clampToTotal(page.total);
  }, [isLoading, page.total, clampToTotal]);
  const [editingTool, setEditingTool] = useState<AgentTool | null>(null);
  const deleteTool = useDeleteTool();

  const allTools = [
    ...customTools.map((tool) => ({
      id: tool.id,
      name: tool.tool_name,
      description: getDisplayDescription(tool.description) || `${tool.http_method} ${tool.endpoint_url}`,
      isBuiltIn: false as const,
      isNative: tool.is_native || false,
      toolData: tool,
    })),
  ];

  const handleDeleteTool = async (tool: AgentTool) => {
    try {
      await deleteTool.mutateAsync(tool);
      // Remove from enabled tools if deleted
      if (enabledToolIds.includes(tool.id)) {
        onToolsSelectionChanged(enabledToolIds.filter((id) => id !== tool.id));
      }
    } catch {
      // useDeleteTool already toasted the error
    }
  };

  const toggleTool = (toolId: string, checked: boolean) => {
    if (checked) {
      onToolsSelectionChanged([...enabledToolIds, toolId]);
    } else {
      onToolsSelectionChanged(enabledToolIds.filter((id) => id !== toolId));
    }
  };

  const isEnabled = (toolId: string) => enabledToolIds.includes(toolId);

  return (
    <div className="space-y-2">
      {allTools.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">No tools available. Create one to get started.</p>
      ) : (
        allTools.map((tool) => (
          <div
            key={tool.id}
            data-testid="tool-row"
            data-tool-name={tool.name}
            className="flex items-center justify-between p-3 bg-muted rounded-md overflow-hidden"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
              <Switch
                checked={isEnabled(tool.id)}
                onCheckedChange={(checked) => toggleTool(tool.id, checked)}
              />
              <div className="flex-1 min-w-0 overflow-hidden">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm truncate">{tool.name}</p>
                  {tool.isNative && <Badge variant="secondary" className="text-[10px] h-5">Native</Badge>}
                </div>
                <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                data-testid="tool-edit"
                onClick={() => {
                  if ("toolData" in tool) {
                    setEditingTool(tool.toolData as AgentTool);
                  }
                }}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="tool-delete">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Tool</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{tool.name}"? This will remove it from all agents.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteTool(tool.toolData)}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))
      )}

      {/* Against the smaller of the default and the chosen size: raising the size
          to fit every tool must not take away the control that raised it, and
          lowering it must not hide the pages it made. */}
      {page.total > Math.min(TOOLS_PAGE_SIZE, list.params.size) && (
        <DataPagination
          page={list.params.page}
          size={list.params.size}
          total={page.total}
          onPageChange={list.setPage}
          onSizeChange={list.setSize}
          label="tools"
        />
      )}

      {editingTool && (
        <EditToolDialog
          tool={editingTool}
          open={!!editingTool}
          onOpenChange={(open) => !open && setEditingTool(null)}
          runtime={runtime}
        />
      )}
    </div>
  );
};
